import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskLine, EASE } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";

const fade = (delay) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, ease: EASE, delay },
});

export const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 60, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 60, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const monoY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);
  const textO = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} onMouseMove={onMove} className="grain relative min-h-[100svh] flex items-center justify-center overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-secondary/60" />
      <motion.div style={{ y: monoY }} className="absolute inset-0 flex items-center justify-center pointer-events-none [perspective:1200px]" aria-hidden>
        <motion.span
          initial={{ opacity: 0, scale: 1.15 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 2.2, ease: EASE }}
          style={{ rotateX: rx, rotateY: ry }}
          className="font-display select-none leading-none tracking-tight whitespace-nowrap text-[34vw] md:text-[30vw] text-foreground/[0.045] [transform-style:preserve-3d]"
        >
          MKSL
        </motion.span>
      </motion.div>

      <motion.div style={{ y: textY, opacity: textO }} className="relative z-10 w-full mx-auto max-w-5xl px-6 md:px-8 py-28 text-center">
        <motion.div {...fade(0.1)} className="mb-10">
          <span className="inline-block px-4 py-1.5 text-xs font-medium tracking-widest uppercase bg-secondary text-muted-foreground rounded-full" data-testid="hero-badge">
            MKSL Consulting
          </span>
        </motion.div>
        <h1 className="font-display text-[2.35rem] leading-[1.08] sm:text-6xl lg:text-[6.5rem] font-medium tracking-tight text-foreground mb-8" data-testid="hero-title">
          <MaskLine animateOnMount delay={0.25}>Angel &amp; Advisor for</MaskLine>
          <MaskLine animateOnMount delay={0.42}>
            <span className="italic">Ambitious Founders</span>
            <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 1.3, duration: 0.6, ease: EASE }}
              className="inline-block ml-1 h-3 w-3 md:h-4 md:w-4 rounded-full bg-accent align-baseline" />
          </MaskLine>
        </h1>
        <motion.p {...fade(0.8)} className="text-base md:text-xl text-muted-foreground max-w-2xl mx-auto mb-12 leading-relaxed" data-testid="hero-subtitle">
          Ich unterstütze Start-ups strategisch, operativ und strukturell – und beteilige mich als langfristiger Partner.
        </motion.p>
        <motion.div {...fade(1)}>
          <button onClick={() => scrollToId("contact")} data-testid="hero-cta-button"
            className="group inline-flex h-14 items-center gap-2 rounded-lg bg-primary px-10 text-lg font-medium text-primary-foreground shadow-lg transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-xl">
            Projekt anfragen
            <ArrowDown className="ml-1 h-5 w-5 transition-transform group-hover:translate-y-1" />
          </button>
        </motion.div>
      </motion.div>

      <motion.div {...fade(1.4)} className="absolute bottom-10 left-1/2 -translate-x-1/2">
        <div className="w-6 h-10 rounded-full border-2 border-muted-foreground/30 flex items-start justify-center p-2">
          <div className="scroll-dot w-1 h-2 bg-muted-foreground/60 rounded-full" />
        </div>
      </motion.div>
    </section>
  );
};
